import React from 'react';
import { Link } from 'react-router-dom';
import { imageUrl } from '../lib/images';
import { useCart, getProductPrice } from '../context/CartContext';

export default function ProductCard({ product }) {
  const { addToCart, openCart } = useCart();

  if (!product) return null;

  const slug = typeof product.slug === 'object' 
    ? (product.slug?.da || product.slug?._ || Object.values(product.slug)[0] || '') 
    : product.slug || '';
  const detailUrl = `/produkter/${slug}`;
  const imageSrc = product.primaert_billede?.[0] || '';
  const price = getProductPrice(product, product.variant_stoerrelse);

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1, product.variant_stoerrelse || 'Standard', price);
  };

  return (
    <div className="group flex flex-col bg-[#FCFAF5] rounded-2xl border border-[#DED3C0] hover:border-[#71806A] hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Top Image Area */}
      <Link
        to={detailUrl}
        className="relative bg-[#F4EFE5] p-6 sm:p-8 flex items-center justify-center min-h-[290px] sm:min-h-[320px] overflow-hidden border-b border-[#DED3C0]"
      >
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
          {product.egenskaber && product.egenskaber.includes('Glutenfri') && (
            <span className="px-2.5 py-1 text-[10px] font-bold tracking-wider uppercase rounded-md bg-[#B98262] text-[#FCFAF5] shadow-xs">
              Glutenfri
            </span>
          )}
          {product.variant_stoerrelse && (
            <span className="px-2.5 py-1 text-[10px] font-semibold tracking-wider rounded-md bg-[#DED3C0]/60 text-[#39463A] shadow-xs border border-[#DED3C0]">
              {product.variant_stoerrelse}
            </span>
          )}
        </div>

        {/* Product Image */}
        {imageSrc ? (
          <img
            src={imageUrl(imageSrc, 'width=700,quality=90,format=auto')}
            alt={product.navn || 'KROMIX produkt'}
            className="h-60 sm:h-68 w-full max-w-[240px] object-contain transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="h-60 sm:h-68 w-full flex items-center justify-center text-[#71806A]/40">
            <i className="fa-light fa-paw text-6xl" aria-hidden="true" />
          </div>
        )}
      </Link>

      {/* Content Area */}
      <div className="flex-1 p-5 flex flex-col justify-between">
        <div>
          {/* Category / Type subtitle */}
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#71806A]">
              {product.produkttype || product.dyretype || 'Hundefoder'}
            </span>
            {product.dyretype && (
              <span className="text-[10px] text-[#71806A] font-medium flex items-center gap-1">
                <i
                  className={product.dyretype.toLowerCase().includes('kat') ? 'fa-solid fa-cat' : 'fa-solid fa-dog'}
                  aria-hidden="true"
                />
                <span>{product.dyretype}</span>
              </span>
            )}
          </div>

          {/* Title in Deep Moss */}
          <h3 className="text-lg font-bold text-[#39463A] group-hover:text-[#71806A] transition-colors leading-snug line-clamp-1">
            <Link to={detailUrl}>
              {product.navn}
            </Link>
          </h3>

          {/* Short description in Deep Moss soft tone */}
          <p className="text-xs text-[#39463A]/80 mt-2 line-clamp-2 leading-relaxed">
            {product.kort_beskrivelse || 'Kvalitetsfoder fremstillet af nøje udvalgte råvarer.'}
          </p>
        </div>

        {/* Price & Action Buttons */}
        <div className="mt-5 pt-4 border-t border-[#DED3C0] space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-[#71806A]">
              Vejl. pris:
            </span>
            {price > 0 ? (
              <span className="text-base font-extrabold text-[#39463A]">
                {price} kr.
              </span>
            ) : (
              <span className="text-xs font-semibold text-[#71806A]">
                Forespørgsel
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Link
              to={detailUrl}
              className="text-center py-2.5 px-2 rounded-xl bg-[#F4EFE5] hover:bg-[#DED3C0]/60 text-[#39463A] text-[11px] font-bold uppercase tracking-wider transition-colors border border-[#DED3C0] flex items-center justify-center gap-1"
            >
              <span>Se info</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>

            <button
              type="button"
              onClick={handleQuickAdd}
              className="py-2.5 px-2 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-[11px] font-bold uppercase tracking-wider transition-all shadow-xs hover:shadow-md flex items-center justify-center gap-1.5 cursor-pointer active:scale-98"
              aria-label={`Læg ${product.navn} i kurven`}
            >
              <i className="fa-solid fa-basket-shopping text-xs" aria-hidden="true" />
              <span>Læg i kurv</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
